// The one module that knows the Studio's URLs: a rename of the hierarchy (decision D4) lands
// here. The environment rides along as the `env` search parameter, kept by the _app route.

import type { SpecRef, State } from "@cosmosai/api-client";
import type { AccountStatus, AccountType } from "@cosmosai/shared";
import type { RouteTarget } from "../../app/registry/nav";
import { BUILT_IN_TABS } from "../../kinds/tabs";

/** Filters of the specs list, all in the URL so a filtered view is a link. */
export interface SpecsSearch {
  /** A kind slug, such as "agent". */
  kind?: string;
  project?: string;
  state?: State;
  q?: string;
}

/** The spec page's own tabs, owned by the kind-tab registry so a kind never takes one. */
export const specTabs = BUILT_IN_TABS;
export type SpecTab = (typeof specTabs)[number];

/** The spec page's tab (built in, or registered for its kind), its version and the compared one. */
export interface SpecSearch {
  tab?: SpecTab | (string & {});
  v?: number;
  compare?: number;
}

/** Filters of the environments board; `refs` (references without version) narrow it to a set. */
export interface EnvironmentsSearch {
  kind?: string;
  project?: string;
  q?: string;
  refs?: string[];
}

/** Filters of the runs list (5c), in the URL. */
export interface RunsSearch {
  agent?: string;
  conversation?: string;
}

/** The chat's agent (a spec name) and conversation (a UUID), in the URL (5d). */
export interface ChatSearch {
  agent?: string;
  conversation?: string;
}

/** Search, filters and page of the users list, in the URL so a filtered view is a link. */
export interface UsersSearch {
  query?: string;
  type?: AccountType;
  status?: AccountStatus;
  /** From 1; absent means the first page. */
  page?: number;
}

const workspaceParams = (workspace: string) => ({ workspace });

export const routes = {
  home: (workspace: string): RouteTarget => ({
    to: "/w/$workspace",
    params: workspaceParams(workspace),
  }),
  specs: (workspace: string, search: SpecsSearch = {}): RouteTarget => ({
    to: "/w/$workspace/specs",
    params: workspaceParams(workspace),
    search: { ...search },
  }),
  /** A spec's page: project-level kinds under /p/<project>, the others under /s. */
  spec: (workspace: string, ref: SpecRef, search: SpecSearch = {}): RouteTarget =>
    ref.project
      ? {
          to: "/w/$workspace/p/$project/$kind/$name",
          params: { workspace, project: ref.project, kind: ref.slug, name: ref.name },
          search: { ...search },
        }
      : {
          to: "/w/$workspace/s/$kind/$name",
          params: { workspace, kind: ref.slug, name: ref.name },
          search: { ...search },
        },
  scopes: (workspace: string): RouteTarget => ({
    to: "/w/$workspace/scopes",
    params: workspaceParams(workspace),
  }),
  environments: (workspace: string, search: EnvironmentsSearch = {}): RouteTarget => ({
    to: "/w/$workspace/environments",
    params: workspaceParams(workspace),
    search: { ...search },
  }),
  apply: (workspace: string): RouteTarget => ({
    to: "/w/$workspace/apply",
    params: workspaceParams(workspace),
  }),
  /** A project's runs, filtered by agent or conversation (5c). */
  runs: (workspace: string, project: string, search: RunsSearch = {}): RouteTarget => ({
    to: "/w/$workspace/p/$project/runs",
    params: { workspace, project },
    search: { ...search },
  }),
  run: (workspace: string, project: string, id: string): RouteTarget => ({
    to: "/w/$workspace/p/$project/runs/$run",
    params: { workspace, project, run: id },
  }),
  /** The chat with a project's `rag` agents (5d). */
  chat: (workspace: string, project: string, search: ChatSearch = {}): RouteTarget => ({
    to: "/w/$workspace/p/$project/chat",
    params: { workspace, project },
    search: { ...search },
  }),
  health: (): RouteTarget => ({ to: "/health" }),
  /** The organization's files, in a folder: not under a workspace, the web API knows organizations only. */
  files: (folder: readonly string[] = []): RouteTarget => ({
    to: "/files",
    search: folder.length > 0 ? { path: folder.join("/") } : {},
  }),
  /** The organization's accounts; the tenant comes from the token, not the URL (ADR 0007). */
  adminUsers: (search: UsersSearch = {}): RouteTarget => ({
    to: "/admin/users",
    search: { ...search },
  }),
  /** The caller's organization: name, alias, domains and counts (super admins only). */
  adminOrganization: (): RouteTarget => ({ to: "/admin/organization" }),
};
